import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for animating elements when they enter the viewport
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Percentage of element that must be visible to trigger animation
 * @param {boolean} options.triggerOnce - Whether to trigger the animation only once
 * @param {string} options.rootMargin - Margin around the root element
 * @returns {Object} - Object with ref and inView state
 */
export const useAnimationOnScroll = (options = {}) => {
    const [inView, setInView] = useState(false);
    const ref = useRef(null);

    const defaultOptions = {
        threshold: 0.2,
        triggerOnce: true,
        rootMargin: '0px 0px -10% 0px'
    };

    const mergedOptions = { ...defaultOptions, ...options };

    useEffect(() => {
        const target = ref.current;

        if (!target) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);

                // If triggerOnce is true, stop observing this element
                if (mergedOptions.triggerOnce) {
                    observer.unobserve(target);
                }
            } else if (!mergedOptions.triggerOnce) {
                setInView(false);
            }
        }, {
            threshold: mergedOptions.threshold,
            rootMargin: mergedOptions.rootMargin
        });

        observer.observe(target);

        // Cleanup observer on component unmount
        return () => {
            if (target) {
                observer.unobserve(target);
            }
        };
    }, [mergedOptions.threshold, mergedOptions.triggerOnce, mergedOptions.rootMargin]);

    return { ref, inView };
};

/**
 * Custom hook for parallax scrolling effect
 * @param {number} speed - Speed of the parallax effect
 * @returns {Object} - Object with ref for the element to apply parallax to
 */
export const useParallaxEffect = (speed = 0.1) => {
    const ref = useRef(null);

    useEffect(() => {
        const element = ref.current;

        if (!element) return;

        const handleScroll = () => {
            const scrollY = window.scrollY;
            const offsetY = scrollY * speed;

            element.style.transform = `translateY(${offsetY}px)`;
        };

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [speed]);

    return { ref };
};